import type { Order } from "@/hooks/useOrders";

export interface TimeWindow {
    startTime: Date;
    endTime: Date;
    label: string;
    orders: Order[];
}

/**
 * Formats a time window label like "12:00 PM - 12:30 PM"
 * Uses local time to match how promise times are displayed in the UI
 */
function formatTimeWindowLabel(startTime: Date, endTime: Date): string {
    const formatTime = (date: Date) => {
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const period = hours >= 12 ? "PM" : "AM";
        const displayHours = hours % 12 || 12;
        const displayMinutes = minutes.toString().padStart(2, "0");
        return `${displayHours}:${displayMinutes} ${period}`;
    };

    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
}

/**
 * Gets the start of the 30-minute window for a given timestamp
 * Uses local time to match how promise times are displayed in the UI
 */
function getWindowStart(timestamp: string, windowMinutes: number): Date {
    const date = new Date(timestamp);
    const hours = date.getHours();
    const minutes = date.getMinutes();

    // Round down to nearest 30-minute window
    // Examples: :00-:29 -> :00, :30-:59 -> :30
    const windowStartMinutes = Math.floor(minutes / windowMinutes) * windowMinutes;

    const windowStart = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        hours,
        windowStartMinutes,
        0,
        0
    );

    return windowStart;
}

/**
 * Groups orders by time windows (e.g., 30-minute intervals)
 * Uses local time for grouping to match the displayed promise times
 * 
 * @param orders - Array of orders to group
 * @param windowMinutes - Size of each time window in minutes (default: 30)
 * @returns Array of time windows with their orders, sorted by start time
 */
export function groupOrdersByTimeWindow(
    orders: Order[],
    windowMinutes: number = 30
): TimeWindow[] {
    // Filter orders that have a valid promise_time
    const ordersWithTime = orders.filter(order => order.promise_time);

    // Group orders by their time window
    const windowMap = new Map<string, TimeWindow>();

    for (const order of ordersWithTime) {
        if (!order.promise_time) continue;

        const windowStart = getWindowStart(order.promise_time, windowMinutes);
        const windowEnd = new Date(windowStart.getTime() + windowMinutes * 60 * 1000);
        const key = windowStart.toISOString();

        if (windowMap.has(key)) {
            windowMap.get(key)!.orders.push(order);
        } else {
            windowMap.set(key, {
                startTime: windowStart,
                endTime: windowEnd,
                label: formatTimeWindowLabel(windowStart, windowEnd),
                orders: [order],
            });
        }
    }

    // Convert to array and sort by start time
    const windows = Array.from(windowMap.values()).sort(
        (a, b) => a.startTime.getTime() - b.startTime.getTime()
    );

    // Sort orders within each window by promise_time
    for (const window of windows) {
        window.orders.sort((a, b) => {
            if (!a.promise_time || !b.promise_time) return 0;
            return new Date(a.promise_time).getTime() - new Date(b.promise_time).getTime();
        });
    }

    return windows;
}

/**
 * Checks if a time window is currently active (current time falls within it)
 */
export function isCurrentWindow(window: TimeWindow): boolean {
    const now = new Date();
    return now >= window.startTime && now < window.endTime;
}

/**
 * Checks if a time window is in the past
 */
export function isPastWindow(window: TimeWindow): boolean {
    const now = new Date();
    return window.endTime < now;
}

/**
 * Checks if a time window is urgent (starting within 15 minutes)
 */
export function isUrgentWindow(window: TimeWindow): boolean {
    const now = new Date();
    const fifteenMinutesFromNow = new Date(now.getTime() + 15 * 60 * 1000);
    return window.startTime <= fifteenMinutesFromNow && window.endTime > now;
}
